/**
 * VideoQuestion — record-live video capture for a `video_ref` question (web).
 * Opens the device camera via getUserMedia, records with MediaRecorder up to
 * `maxDurationSec`, enforces `maxMb`, and uploads the clip (multipart) to
 * `POST /videos` (→ OneDrive when the tenant has Graph configured). The stored
 * answer is the returned video id. No file uploads — capture only.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Video, Square, RefreshCw, Check, X, AlertTriangle, SwitchCamera } from 'lucide-react';
import { API_BASE } from '@/lib/api';
import { getAccessToken } from '@/lib/api';
import { toast } from 'sonner';

interface Props {
  instructionId: string;
  token?: string;
  questionCode: string;
  caption?: string;
  value: string;                 // stored video id (answer)
  onChange: (videoId: string) => void;
  testId: string;
  maxMb?: number;
  maxDurationSec?: number;
  facing?: 'any' | 'front' | 'back';
  allowSwitch?: boolean;
}

export const VideoQuestion: React.FC<Props> = ({
  instructionId, token, questionCode, caption, value, onChange, testId,
  maxMb = 50, maxDurationSec = 30, facing = 'any', allowSwitch = true,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [live, setLive] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [curFacing, setCurFacing] = useState<'user' | 'environment'>(facing === 'front' ? 'user' : 'environment');

  const stopStream = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setLive(false);
    setRecording(false);
  }, []);

  useEffect(() => () => stopStream(), [stopStream]);

  const startCamera = useCallback(async (which?: 'user' | 'environment') => {
    setError(null);
    const want = which ?? curFacing;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('This device/browser does not support video recording.');
      return;
    }
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: want } }, audio: true,
      });
      streamRef.current = stream;
      setCurFacing(want);
      setLive(true);
      setElapsed(0);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.muted = true; videoRef.current.play().catch(() => {}); } }, 50);
    } catch (e: unknown) {
      const name = (e as { name?: string })?.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') setError('Camera/microphone permission denied. Enable access in your browser settings to record.');
      else if (name === 'NotFoundError' || name === 'OverconstrainedError') setError('No camera found on this device.');
      else setError('Could not start the camera. Please try again.');
    }
  }, [curFacing]);

  const uploadClip = useCallback(async (blob: Blob, durationSec: number) => {
    if (blob.size > Math.max(1, maxMb) * 1024 * 1024) {
      setError(`Clip too large (${(blob.size / 1024 / 1024).toFixed(1)} MB). Limit is ${maxMb} MB — record a shorter clip.`);
      return;
    }
    setBusy(true);
    try {
      const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
      const form = new FormData();
      form.append('instruction_id', instructionId);
      form.append('question_code', questionCode);
      form.append('caption', caption || '');
      form.append('duration_sec', String(Math.round(durationSec)));
      form.append('max_mb', String(maxMb));
      form.append('file', blob, `clip.${ext}`);
      const headers: Record<string, string> = {};
      const jwt = getAccessToken();
      if (!token && jwt) headers['Authorization'] = `Bearer ${jwt}`;
      const res = await fetch(`${API_BASE}/videos`, { method: 'POST', body: form, headers });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error((j as { detail?: string })?.detail || 'Upload failed');
      }
      const data = await res.json();
      onChange(data.id);
      toast.success('Video recorded');
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Could not save the video. Please re-record.');
      toast.error('Could not save the video.');
    } finally { setBusy(false); }
  }, [caption, instructionId, maxMb, onChange, questionCode, token]);

  const stopRecording = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { recorderRef.current?.stop(); } catch { /* ignore */ }
    setRecording(false);
  }, []);

  const startRecording = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    chunksRef.current = [];
    const mime = MediaRecorder.isTypeSupported('video/mp4') ? 'video/mp4'
      : MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm';
    const rec = new MediaRecorder(stream, { mimeType: mime });
    recorderRef.current = rec;
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime.split(';')[0] });
      const secs = elapsed;
      stopStream();
      void uploadClip(blob, secs);
    };
    rec.start();
    setRecording(true);
    setElapsed(0);
    timerRef.current = setInterval(() => {
      setElapsed((s) => {
        const next = s + 1;
        if (next >= maxDurationSec) { stopRecording(); return maxDurationSec; }
        return next;
      });
    }, 1000);
  }, [elapsed, maxDurationSec, stopRecording, stopStream, uploadClip]);

  const switchCamera = useCallback(() => {
    void startCamera(curFacing === 'user' ? 'environment' : 'user');
  }, [curFacing, startCamera]);

  const reRecord = useCallback(() => { onChange(''); void startCamera(); }, [onChange, startCamera]);

  // Captured state
  if (value && !live) {
    return (
      <div className="space-y-2" data-testid={testId}>
        <div className="flex items-center gap-2 text-sm text-green-700 font-body">
          <Check size={16} /> Video recorded
        </div>
        <video src={`${API_BASE}/videos/${value}`} controls playsInline className="w-full max-w-sm rounded-md border border-border/60 bg-black" data-testid={`${testId}-player`} />
        <button type="button" onClick={reRecord} data-testid={`${testId}-rerecord`}
          className="inline-flex items-center gap-1 px-3 h-8 rounded-md border border-raptor-blue/40 text-raptor-blue text-sm">
          <RefreshCw size={14} /> Re-record
        </button>
      </div>
    );
  }

  // Live preview / recording
  if (live) {
    return (
      <div className="space-y-2" data-testid={testId}>
        <div className="relative max-w-sm">
          <video ref={videoRef} playsInline muted className="w-full rounded-md border border-border/60 bg-black" />
          {recording && (
            <div className="absolute top-2 left-2 flex items-center gap-1 rounded bg-red-600/90 px-2 py-0.5 text-[11px] font-semibold text-white" data-testid={`${testId}-timer`}>
              <span className="h-2 w-2 rounded-full bg-white animate-pulse" /> {elapsed}s / {maxDurationSec}s
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!recording ? (
            <button type="button" onClick={startRecording} disabled={busy} data-testid={`${testId}-record`}
              className="inline-flex items-center gap-1 px-3 h-9 rounded-md bg-red-600 text-white text-sm font-medium disabled:opacity-60">
              <Video size={15} /> Record
            </button>
          ) : (
            <button type="button" onClick={stopRecording} data-testid={`${testId}-stop`}
              className="inline-flex items-center gap-1 px-3 h-9 rounded-md bg-raptor-navy text-white text-sm font-medium">
              <Square size={14} /> Stop
            </button>
          )}
          <button type="button" onClick={stopStream} data-testid={`${testId}-cancel`}
            className="inline-flex items-center gap-1 px-3 h-9 rounded-md border border-border/60 text-raptor-navy/70 text-sm">
            <X size={15} /> Cancel
          </button>
          {allowSwitch && facing === 'any' && !recording && (
            <button type="button" onClick={switchCamera} data-testid={`${testId}-switch`}
              className="inline-flex items-center gap-1 px-3 h-9 rounded-md border border-border/60 text-raptor-navy/70 text-sm ml-auto">
              <SwitchCamera size={15} /> {curFacing === 'user' ? 'Rear' : 'Front'}
            </button>
          )}
        </div>
        {busy && <div className="text-xs text-raptor-navy/60">Saving video…</div>}
      </div>
    );
  }

  // Idle
  return (
    <div className="space-y-2" data-testid={testId}>
      <button type="button" onClick={() => void startCamera()} disabled={busy} data-testid={`${testId}-open`}
        className="inline-flex items-center gap-1 px-3 h-9 rounded-md bg-raptor-gradient text-white text-sm font-medium disabled:opacity-60">
        <Video size={15} /> {busy ? 'Saving…' : 'Record video'}
      </button>
      <div className="text-[11px] text-raptor-navy/50">Up to {maxDurationSec}s · max {maxMb} MB</div>
      {error && (
        <div className="flex items-start gap-1 text-xs text-red-600 font-body">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}
    </div>
  );
};

export default VideoQuestion;
